import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate , useLocation } from 'react-router-dom';
import { OperationNavStrip, type OperationNav } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';

const POLL_INTERVAL_MS = 3_000;
const POLL_MAX_MS = 5 * 60_000;
import type { ModelOutput } from '../../types';
import {
    OperationDetailPage,
    ContextGrid,
    UserProfileCard,
    MetadataCard,
    MetaItem,
    QuestionCard,
    StudentWorkCard,
    RunsTable,
    RunDetailCard,
    formatLatency,
    usePromptLookup,
    buildDefaultRunColumns,
    buildRunDetailHeading,
    buildRunSummary,
    getRunError,
} from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, getRawOutputText } from '@shared-ui/parsed-feedback';
import './SolutionAnalysisDetail.css';

export function ConceptLabDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    /** The page of rows this detail was opened from, if it was opened from one.
     *  Absent on a direct link, which is why the strip is conditional. */
    const opNav = (location.state as { nav?: OperationNav } | null)?.nav;
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

    // Cap auto-refresh at POLL_MAX_MS while concept-lab is generating.
    const mountedAtRef = useRef<number>(Date.now());

    const { data: entry, isLoading, error, refetch } = useQuery({
        queryKey: ['conceptLabRequest', id],
        queryFn: () => adminApi.getConceptLabRequest(id!),
        enabled: !!id,
        refetchInterval: (query) => {
            const status = query.state.data?.conceptLab?.status;
            const elapsed = Date.now() - mountedAtRef.current;
            return status === 'generating' && elapsed < POLL_MAX_MS ? POLL_INTERVAL_MS : false;
        },
    });

    // conceptLab.modelInputId = concept lab's own LLM runs (for ModelOutput table)
    const clModelInputId = entry?.conceptLab?.modelInputId;
    const { data: inputWithOutputs } = useQuery({
        queryKey: ['modelInput', clModelInputId],
        queryFn: () => adminApi.getModelInput(clModelInputId!),
        enabled: !!clModelInputId,
    });

    const { data: prompts } = useQuery({
        queryKey: ['prompts', 'includeArchived'],
        queryFn: () => adminApi.getPrompts({ includeArchived: true }),
    });

    const { getPromptName, getPromptType } = usePromptLookup(prompts);

    const runs = inputWithOutputs?.outputs ?? [];

    useEffect(() => {
        if (selectedRunId || runs.length === 0) return;
        const primary = runs.find(r => r.isPrimary) ?? runs[0];
        if (primary) setSelectedRunId(primary.id);
    }, [runs, selectedRunId]);

    const selectedRun = runs.find(r => r.id === selectedRunId);

    if (isLoading) {
        return <div className="operation-detail-page"><div className="loading">Loading concept lab details...</div></div>;
    }
    if (error || !entry) {
        return (
            <div className="operation-detail-page">
                <div className="error">Error loading concept lab: {(error as Error)?.message || 'Not found'}</div>
                <button onClick={() => navigate('/submissions?tab=conceptlab')}>Back to Concept Lab</button>
            </div>
        );
    }

    const elapsedMs = Date.now() - mountedAtRef.current;
    const pollingExhausted = entry.conceptLab.status === 'generating' && elapsedMs >= POLL_MAX_MS;

    if (entry.conceptLab.status === 'generating') {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h2>Concept lab generation in progress…</h2>
                <p>The LLM call is still in flight. The page will refresh automatically.</p>
                <p style={{ color: '#888', fontSize: '0.9rem' }}>
                    Submission: <code>{entry.id}</code>
                </p>
                {pollingExhausted && (
                    <>
                        <p style={{ color: '#888', marginTop: '1rem' }}>
                            Auto-refresh stopped after {Math.round(POLL_MAX_MS / 60_000)} minutes — generation may be stuck.
                        </p>
                        <button type="button" style={{ marginTop: '0.5rem' }} onClick={() => { mountedAtRef.current = Date.now(); refetch(); }}>
                            Refresh
                        </button>
                    </>
                )}
            </div>
        );
    }

    if (entry.conceptLab.status === 'failed') {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h2>Concept lab generation failed</h2>
                <p>The LLM call did not complete for submission <code>{entry.id}</code>.</p>
                <button type="button" style={{ marginTop: '1rem' }} onClick={() => refetch()}>
                    Refresh
                </button>
            </div>
        );
    }

    const cl = entry.conceptLab;
    // After the status checks above, completed entries have `response` set.
    if (!cl.response) {
        return <div className="operation-detail-page"><div className="error">Concept lab entry is incomplete (no response stored).</div></div>;
    }
    const promptLookup = { getPromptName, getPromptType };
    const runColumns = buildDefaultRunColumns<ModelOutput>(promptLookup, {
        returnTo: { label: 'Concept Lab Detail' },
    });
    const runDetailHeading = selectedRun && buildRunDetailHeading(selectedRun, promptLookup);
    const runSummary = selectedRun && buildRunSummary(selectedRun);
    const runError = selectedRun ? getRunError(selectedRun) : undefined;
    const selectedPromptType = selectedRun ? getPromptType(selectedRun.parameters.promptId) : undefined;
    const selectedRunRawResponse = selectedRun ? getRawOutputText(selectedRun) : null;
    const parsedFeedbackHasContent = !!(selectedRun && (selectedRun.parsedAssessment || selectedRun.extractedData || selectedRunRawResponse));
    // Inner parsed body — the shared ModelOutputView (via RunDetailCard) owns the collapsible wrapper.
    const parsedFeedbackBody = selectedRun && parsedFeedbackHasContent ? (
        <ParsedFeedbackBody
            promptType={selectedPromptType}
            feedback={selectedRun.parsedAssessment}
            extractedData={selectedRun.extractedData}
            rawResponse={selectedRunRawResponse}
        />
    ) : undefined;

    return (
        <OperationDetailPage
            title="Concept Lab Details"
            id={entry.id}
            idContainer="submissions"
            backLabel="Concept Lab"
            navSlot={opNav && <OperationNavStrip nav={opNav} noun="Entry" />}
            onBack={() => navigate('/submissions?tab=conceptlab')}
            context={
                <ContextGrid
                    left={
                        <UserProfileCard
                            user={{
                                id: entry.user?.id,
                                name: entry.user?.name,
                                email: entry.user?.email,
                                grade: entry.user?.grade,
                                // Pipeline config shown in the Concept Lab Result card — omit here.
                            }}
                            onViewUser={uid => navigate(`/users/${uid}`)}
                        />
                    }
                    right={
                        <MetadataCard title="Concept Lab Result">
                            <MetaItem label="Pipeline Config"><span className="tag">{cl.pipelineConfig || '—'}</span></MetaItem>
                            <MetaItem label="Subject"><span className="tag">{entry.question?.subject || '-'}</span></MetaItem>
                            <MetaItem label="Topic"><span className="tag">{entry.question?.topic || '-'}</span></MetaItem>
                            <MetaItem label="Provider"><span className="tag">{cl.provider || '-'}</span></MetaItem>
                            <MetaItem label="Latency">{formatLatency(cl.latencyMs)}</MetaItem>
                            <MetaItem label="Generated">
                                <span className="datetime">{format(new Date(cl.generatedAt), 'MMM dd, HH:mm:ss')}</span>
                            </MetaItem>
                            <MetaItem label="Interactions">{cl.interactions?.length || 0}</MetaItem>
                        </MetadataCard>
                    }
                />
            }
            input={
                <>
                    <QuestionCard
                        question={{
                            id: entry.questionId,
                            questionText: entry.question?.questionText,
                            questionDiagramUrl: entry.question?.questionDiagramUrl,
                            figures: entry.question?.figures,
                            questionType: entry.question?.questionType,
                            parts: entry.question?.parts,
                        }}
                        onViewQuestion={qid => navigate(`/questions?id=${qid}`)}
                    />
                    <StudentWorkCard
                        work={entry}
                        questionOptions={entry.question?.options}
                    />
                </>
            }
            runs={runs.length > 0 && (
                <RunsTable
                    rows={runs}
                    columns={runColumns}
                    rowKey={r => r.id}
                    selectedRowId={selectedRunId}
                    onSelect={r => setSelectedRunId(r.id)}
                    getRowClass={r => [r.isPrimary ? 'primary' : '', r.errors?.length ? 'has-error' : ''].filter(Boolean).join(' ')}
                />
            )}
            runDetail={
                <>
                    {/* Concept Lab Response Summary */}
                    <div className="card">
                        <h3>Concept Spotlight</h3>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{cl.response.conceptSpotlight}</p>
                    </div>

                    <div className="card">
                        <h3>Personalized Narrative</h3>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{cl.response.personalizedNarrative}</p>
                    </div>

                    {cl.response.probingQuestions.length > 0 && (
                        <div className="card">
                            <h3>Probing Questions ({cl.response.probingQuestions.length})</h3>
                            {cl.response.probingQuestions.map((pq, i, arr) => {
                                const interaction = cl.interactions?.find(ix => ix.probingQuestionId === pq.id);
                                return (
                                    <div key={pq.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: i < arr.length - 1 ? '1px solid var(--border-color, #e0e0e0)' : undefined }}>
                                        <p><strong>Q{i + 1}:</strong> {pq.question}</p>
                                        <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                                            {pq.options.map((opt, oi) => (
                                                <li key={oi} style={{ color: oi === pq.correctOptionIndex ? 'var(--color-success, green)' : undefined, fontWeight: oi === pq.correctOptionIndex ? 600 : undefined }}>
                                                    {opt}
                                                </li>
                                            ))}
                                        </ul>
                                        <p style={{ fontSize: '0.85em', color: 'var(--text-tertiary)' }}><em>Explanation:</em> {pq.explanation}</p>
                                        {interaction && (
                                            <p style={{ fontSize: '0.85em', marginTop: 4 }}>
                                                <span className={`badge ${interaction.isCorrect ? 'badge-gentle' : 'badge-direct'}`}>
                                                    {interaction.isCorrect ? 'Correct' : 'Incorrect'}
                                                </span>
                                                {' '}answered option {interaction.studentAnswer + 1} in {(interaction.timeToAnswerMs / 1000).toFixed(1)}s
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {cl.response.mistakeTargetedQuestions.length > 0 && (
                        <div className="card">
                            <h3>Mistake-Targeted Questions ({cl.response.mistakeTargetedQuestions.length})</h3>
                            {cl.response.mistakeTargetedQuestions.map((mtq, i) => (
                                <div key={i} style={{ marginBottom: 12 }}>
                                    <p><strong>{mtq.questionText}</strong></p>
                                    <p style={{ fontSize: '0.85em', color: 'var(--text-tertiary)' }}>
                                        <span className="badge">{mtq.source}</span> {mtq.relevanceReason}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {cl.response.relatedConcepts.length > 0 && (
                        <div className="card">
                            <h3>Related Concepts ({cl.response.relatedConcepts.length})</h3>
                            {cl.response.relatedConcepts.map((rc, i) => (
                                <div key={i} style={{ marginBottom: 12 }}>
                                    <p><strong>{rc.conceptName}</strong> — {rc.relationship}</p>
                                    <p style={{ fontSize: '0.85em', color: 'var(--text-tertiary)' }}>{rc.briefDescription}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* LLM Run Detail (prompt/response) */}
                    {selectedRun && (
                        <RunDetailCard
                            heading={runDetailHeading}
                            error={runError}
                            errorRecords={selectedRun.errorRecords}
                            summary={runSummary}
                            systemPrompt={selectedRun.systemPromptText}
                            userPrompt={selectedRun.promptText}
                            rawResponse={selectedRunRawResponse}
                            rawOutputFull={selectedRun.rawOutputFull}
                            reasoningSummary={selectedRun.reasoningSummaryText}
                            modelInputId={selectedRun.inputId}
                            modelOutputId={selectedRun.id}
                            parsed={parsedFeedbackBody}
                        />
                    )}
                </>
            }
        />
    );
}
